import TranslatableText from "@components/translations/TranslatableText";
import Skeleton from "react-loading-skeleton";
import { transformMsaToPolicy } from "./utils";
import { useAppSelector } from "@app/hooks";
import { selectMsaJobsByProject } from "@app/features/msa/msaSlice";
import type { RootState } from "@app/store";
import { useLandCoverChange } from "./useLandCoverChange";

interface PolicyIndicatorsSectionProps {
  projectId?: string;
}

const PolicyIndicatorsSection = ({ projectId }: PolicyIndicatorsSectionProps) => {
  const msaJobs = useAppSelector((state: RootState) =>
    projectId ? selectMsaJobsByProject(state, projectId) : null
  );

  const {
    projectAreaHa,
    lucAreaHa,
    isLoading: lucLoading,
    error: lucError,
  } = useLandCoverChange(projectId);

  // Transform the MSA data to policy data
  const policyData = transformMsaToPolicy(msaJobs?.project ?? null);

  // Determine loading state based on job status
  const msaLoading =
    msaJobs?.project?.status === "starting" ||
    msaJobs?.project?.status === "in_progress";

  // LUC metrics shown only when both baseline and project results exist
  const hasBothMsaResults =
    msaJobs?.baseline?.status === "completed" &&
    msaJobs?.project?.status === "completed" &&
    msaJobs?.baseline?.result?.zip_url &&
    msaJobs?.project?.result?.zip_url;

  // Check if data is missing
  const hasData = policyData !== null;
  const projectFailed = msaJobs?.project?.status === "failed";
  const hasError = projectFailed;

  return (
    <div className="msa-container">
      <h3 className="fs-18 ff-medium">
        <TranslatableText translationKey="map.area_impact_summary" />
      </h3>

      {/* Alert messages for missing or error states */}
      {!msaLoading && hasError && (
        <div className="mb-2" style={{
          padding: '8px 12px',
          fontSize: '14px',
          backgroundColor: '#fde8e8',
          border: '1px solid #f5c2c2',
          borderRadius: '6px',
          color: '#9c0004'
        }}>
          {projectFailed && (
            <div>
              <TranslatableText translationKey="map.msa_calculation_failed" />: {msaJobs?.project?.error}
            </div>
          )}
        </div>
      )}
      {lucError && (
        <div className="mb-2" style={{
          padding: '8px 12px',
          fontSize: '14px',
          backgroundColor: '#fde8e8',
          border: '1px solid #f5c2c2',
          borderRadius: '6px',
          color: '#9c0004'
        }}>
          <TranslatableText translationKey="map.area_of_land_cover_change" />: {lucError}
        </div>
      )}
      {!msaLoading && !hasError && !hasData && (
        <div className="mb-2" style={{
          padding: '8px 12px',
          fontSize: '14px',
          backgroundColor: '#e5ecf4',
          border: '1px solid #b8d4e8',
          borderRadius: '6px',
          color: '#164777'
        }}>

          <TranslatableText translationKey="map.no_msa_results_available" />
        </div>
      )}
      {msaLoading && (
        <div className="mb-2" style={{ 
          padding: '8px 12px', 
          fontSize: '14px',
          backgroundColor: '#fff9e6',
          border: '1px solid #ffe8b3',
          borderRadius: '6px',
          color: '#b8860b'
        }}>
          <TranslatableText translationKey="map.msa_calculation_in_progress" />
        </div>
      )}

      {/* Project Area: use raster-based when LUC available, else API total_area_ha */}
      {hasBothMsaResults && (
        <div className="d-flex gap-8 mb-2">
          <TranslatableText translationKey="map.project_area" suffix=" [ha]:" />
          {lucLoading ? (
              <Skeleton height={24} width={80} borderRadius={6} />
          ) : projectAreaHa !== null ? (
            <div className="fs-16 ff-medium-ext no-wrap">
              {projectAreaHa.toFixed(2)}
            </div>
          ) : null}
        </div>
      )}

      {/* Area of Land Cover Change: only when both baseline and project results */}
      {hasBothMsaResults && (
        <div className="d-flex gap-8 mb-2">
          <TranslatableText translationKey="map.area_of_land_cover_change" suffix=" [ha]:" />
          {lucLoading ? (
              <Skeleton height={24} width={80} borderRadius={6} />
          ) : lucAreaHa !== null ? (
            <div className="fs-16 ff-medium-ext no-wrap">
              {lucAreaHa.toFixed(2)}
            </div>
          ) : null}
        </div>
      )}

      {/* Total Project Area (API-based, when LUC not applicable) */}
      {!hasBothMsaResults && (
        <div className="d-flex gap-8">
          <TranslatableText translationKey="map.total_project_area" suffix=" [ha]:" />
          {msaLoading ? (
            <div className="py-2">
              <Skeleton height={150} borderRadius={8} />
            </div>
          ) : hasData ? (
            <div className="fs-16 ff-medium-ext no-wrap">
              {policyData?.area?.toLocaleString() || 0}
            </div>
          ) : (
            <div className="py-2" style={{ padding: '16px', color: '#666' }}>
              No data available
            </div>
          )}
        </div>
      )}
    </div>);
}

export default PolicyIndicatorsSection;